import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import { isEmpty } from "lodash/fp";
import Chip from "@material-ui/core/Chip";
import { Grid, TextField } from "@material-ui/core";
import { useTypedDispatch } from "../../../app/state";
import SearchQueryParams from "../../../routes/SearchQueryParams";
import ReportingDialog from "../../../components/ReportingDialog";
import { selectGlobalSettings } from "../../../store/settingsStore/settingsSelectors";
import { GenericModal } from "../../../app/components/ui/generic-modal/GenericModal";
import savedSearchValidation from "./savedSearchValidation";
import ColumnsSelector, { deviceColumnList } from "../../../components/ColumnsSelector";
import HorizontalPanel from "../../../components/HorizontalPanel";
import MultiChips from "../../../components/MiltiChips";
import ReportSettings from "../../../components/ReportSettings";
import { Toolbar } from "../../../app/components";
import { saveSearch, updateSavedSearch } from "../../../store/searchStore/searchRequests";
import SavedSearchModel from "../../../models/SavedSearchModel";
import { selectAllSavedSearchList, selectCurrentSavedSearch } from "../../../store/searchStore/searchSelectors";
import { useSnackbar } from "../../../app/shared/snackbar-context/snackbar.context";
import useNavigator from "../../../hooks/navigator.hook";

import classes from './index.module.scss';

function extractInitialValues(search: SavedSearchModel) {
  const data = JSON.parse(search.savedData);

  return {
    name: search.name,

    fieldList: data.reportSettings.fieldList,
    exportDefaultUnits: data.reportSettings.exportDefaultUnits,
    exportEnumerationDivider: data.reportSettings.exportEnumerationDivider,
    exportFieldDivider: data.reportSettings.exportFieldDivider,
    exportDecimalDivider: data.reportSettings.exportDecimalDivider,
    exportDecimalPlaceDigits: data.reportSettings.exportDecimalPlaceDigits,
    exportFormatDate: data.reportSettings.exportFormatDate,

    columnList: data.columnList,
  }
}

const SavedSearchDialog = (props: { params?: SearchQueryParams, disabled: boolean }) => {
  const dispatch = useTypedDispatch();
  const { openSnackbar } = useSnackbar();
  const { navigateToSearchPage } = useNavigator();
  const savedSearchList = useSelector(selectAllSavedSearchList);
  const currentSavedSearch = useSelector(selectCurrentSavedSearch);
  const settings = useSelector(selectGlobalSettings);
  const reportingSettings = ReportingDialog.getInitialValues(settings);
  const columnsSettings = ColumnsSelector.getInitialColumns();
  const [ isOpened, setIsOpened ] = useState(false);

  const allDeviceColumns = Object.keys(deviceColumnList).map((key) => {
    return {
      id: key,
      name: deviceColumnList[key],
    }
  })

  const getSelectedColumns = () => {
    return Object.keys(columnsSettings).map((key) => {
      return columnsSettings[key] && key;
    }).filter(Boolean);
  }

  const savedSearchFormik = useFormik({
    initialValues: currentSavedSearch ? extractInitialValues(currentSavedSearch) : {
      name: '',
      ...reportingSettings,
      columnList: getSelectedColumns(),
    },
    onSubmit: () => {},
    validationSchema: savedSearchValidation,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
  });

  const selectedColumns = savedSearchFormik.values.columnList.map((field) => {
    return {
      id: field,
      name: field,
    }
  });

  const onSaveSearchOpen = () => {
    setIsOpened(true);
  }

  const onClose = () => {
    setIsOpened(false);
    savedSearchFormik.resetForm();
  }

  const checkIsEdit = (name: string) => {
    const found = savedSearchList.find((search) => search.name === name);

    return found?.id || 0;
  }

  const selectSavedSearch = (savedSearch: SavedSearchModel) => {
    const data = JSON.parse(savedSearch.savedData);
    const { searchParams, columnList, reportSettings } = data;

    ColumnsSelector.saveColumnsArray(columnList);
    ReportingDialog.saveReportValues(reportSettings);
    setTimeout(() => {
      navigateToSearchPage({ ...searchParams, savedSearchId: savedSearch.id });
    }, 15)
  }

  const onSave = async () => {
    const errors = await savedSearchFormik.validateForm();

    if (isEmpty(errors)) {
      const { name, columnList, ...reportSettings } = savedSearchFormik.values;
      const editId = checkIsEdit(name);
      onClose();

      const savedData = JSON.stringify({
        searchParams: props.params,
        columnList,
        reportSettings,
      });

      if (editId) {
        dispatch(
          updateSavedSearch({
            id: editId,
            name,
            savedData,
          })
        ).then((action) => {
          const savedSearch = action.payload as SavedSearchModel;

          console.log(savedSearch);

          selectSavedSearch(savedSearch);

          openSnackbar({
            title: 'Operation was successful',
            message: 'Search has been updated',
            severity: 'success',
          })
        })
      } else {
        dispatch(
          saveSearch({
            name,
            savedData,
          })
        ).then((action) => {
          const savedSearch = action.payload as SavedSearchModel;

          console.log(savedSearch);

          selectSavedSearch(savedSearch);

          openSnackbar({
            title: 'Operation was successful',
            message: 'Search has been saved',
            severity: 'success',
          })
        })
      }
    }
  }

  return (
    <>
      <Chip
        disabled={props.disabled}
        label="Save"
        color={"primary"}
        onClick={onSaveSearchOpen}
      />
      <GenericModal
        isOpen={isOpened}
        closeModal={onClose}
        hideTitleSection
        title="Report"
        fullSize
        actionList={[
          {
            title: 'Save',
            handler: onSave,
            color: 'secondary',
          },
          {
            title: 'Close',
            handler: onClose,
            color: 'secondary',
          },
        ]}
      >
        <HorizontalPanel size={HorizontalPanel.SIZES.WHOLE_SPACE}>
          <HorizontalPanel size={HorizontalPanel.SIZES.SMALL} spacing={2} className={classes.form}>
            <Toolbar title="Save current search" />
            <Grid item xs={12}>
              <TextField
                label='Search name'
                variant='outlined'
                name='name'
                fullWidth
                value={savedSearchFormik.values.name}
                onChange={savedSearchFormik.handleChange}
                helperText={savedSearchFormik.errors.name}
                error={Boolean(savedSearchFormik.errors.name)}
              />
            </Grid>
            <Toolbar title="Columns in saved search" />
            <Grid item xs={12}>
              <MultiChips
                name='columnList'
                items={allDeviceColumns}
                selectedItems={selectedColumns}
                onChange={
                  (values: string[]) =>

                    savedSearchFormik.setFieldValue(
                      'columnList',
                      values)

                }
                helperText={savedSearchFormik.errors.columnList}
                error={Boolean(savedSearchFormik.errors.columnList)}
              />
            </Grid>
          </HorizontalPanel>
        </HorizontalPanel>
        <ReportSettings settingsFormik={savedSearchFormik} />
      </GenericModal>
    </>
  );
}

export default SavedSearchDialog;